import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { listInvoices } from "../../services/invoices";
import DataTable from "../../components/ui/DataTable";

const Invoices = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState({ status: "", search: "" });

  useEffect(() => {
    loadInvoices();
  }, [pagination.page, filters]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await listInvoices({
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status || undefined,
      });

      console.log("Invoices API Response:", response);

      if (response.success && response.data) {
        const invoicesList = response.data.invoices || [];
        console.log("Setting invoices:", invoicesList);
        setInvoices(invoicesList);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0,
        }));
      } else {
        console.warn("Unexpected response structure:", response);
        // Try to handle different response structures
        if (response.data && Array.isArray(response.data)) {
          setInvoices(response.data);
        } else if (response.invoices && Array.isArray(response.invoices)) {
          setInvoices(response.invoices);
        }
      }
    } catch (error) {
      console.error("Failed to load invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: "bg-gray-100 text-gray-700",
      SUBMITTED: "bg-blue-100 text-blue-700",
      AUTHORISED: "bg-green-100 text-green-700",
      PAID: "bg-emerald-100 text-emerald-700",
      REFUNDED: "bg-amber-100 text-amber-800",
      VOIDED: "bg-red-100 text-red-700",
      DELETED: "bg-gray-100 text-gray-500",
    };
    return badges[status] || "bg-gray-100 text-gray-700";
  };

  const columns = [
    {
      header: "Invoice Number",
      accessor: "invoiceNumber",
      render: (value, row) => (
        <button
          onClick={() => navigate(`/invoices/${row._id}`)}
          className="text-emerald-600 hover:text-emerald-700 font-medium"
        >
          {row.invoiceNumber || "N/A"}
        </button>
      ),
    },
    {
      header: "Reference",
      accessor: "reference",
      render: (value, row) => (
        <button
          onClick={() =>
            row.licenceId &&
            navigate(`/licences/${row.licenceId._id || row.licenceId}`)
          }
          className="text-gray-600 hover:text-gray-900"
        >
          {row.reference || "N/A"}
        </button>
      ),
    },
    {
      header: "Customer",
      accessor: "customerId",
      render: (value, row) => {
        const customer = row.customerId;
        if (!customer) return "N/A";
        return (
          `${customer.firstName || ""} ${customer.lastName || ""}`.trim() ||
          customer.email ||
          "N/A"
        );
      },
    },
    {
      header: "Amount",
      accessor: "total",
      render: (value, row) =>
        `${row.currency || "GBP"} ${(row.total || 0).toFixed(2)}`,
    },
    {
      header: "Status",
      accessor: "status",
      render: (value, row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(row.status)}`}
        >
          {row.status}
        </span>
      ),
    },
    {
      header: "Date",
      accessor: "date",
      render: (value, row) =>
        row.date ? new Date(row.date).toLocaleDateString() : "N/A",
    },
    // {
    //   header: 'Sync Status',
    //   accessor: 'syncStatus',
    //   render: (value, row) => (
    //     <span className={`px-2 py-1 rounded-full text-xs font-medium ${
    //       row.syncStatus === 'synced' ? 'bg-green-100 text-green-700' :
    //       row.syncStatus === 'error' ? 'bg-red-100 text-red-700' :
    //       'bg-yellow-100 text-yellow-700'
    //     }`}>
    //       {row.syncStatus || 'pending'}
    //     </span>
    //   )
    // }
  ];

  const actionMenuItems = (row) => [
    {
      label: "View Details",
      onClick: () => navigate(`/invoices/${row._id}`),
    },
    {
      label: "Download PDF",
      onClick: () => {
        const link = document.createElement("a");
        link.href = `/api/admin/invoices/${row._id}/pdf`;
        link.download = `invoice-${row.invoiceNumber}.pdf`;
        link.click();
      },
    },
  ];

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and track all invoices
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              <option value="">All</option>
              <option value="DRAFT">Draft</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="AUTHORISED">Authorised</option>
              <option value="PAID">Paid</option>
              <option value="VOIDED">Voided</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={invoices}
        loading={loading}
        actionMenuItems={actionMenuItems}
        pagination={pagination}
        currentPage={pagination.page}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default Invoices;
